import { useEffect, useState } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { checkIfUserHasPermissionToAccessConfluence } from '../permission-check/index.tsx';
import {
	useIsInactiveConfluenceUser,
	useIsJiraAccountAgeEligible,
	useIsNewConfluenceUser,
	useHasNotInteractedWithNudge,
} from '../traits/index.tsx';

/**
 * This suite of experiments has two potential groups of eligible users.
 *
 * Group 1: is used for all four touchpoints, and calculates eligibility based on the following checks:
 * - user has been active in Jira for at least 7 days
 * - user has been inactive in Confluence for at least 30 days
 * - user has permission to access Confluence
 *
 * Group 2: is used for the Jira search touchpoint only, and calculates eligibility based on the following checks:
 * - user has been active in Jira for at least 7 days
 * - user has never been activated in Confluence
 * - user has permission to access Confluence
 * @returns {object} containing three booleans, one for whether the user has opened the Confluence search panel,
 *  one for first group eligibility and one for second group eligibility
 */

export const useConfluenceReactivationExperiment = () => {
	const cloudId = useCloudId();
	const [permitted, setPermitted] = useState<boolean>();

	const [passesJiraAgeCondition] = useIsJiraAccountAgeEligible();
	const [passesConfluenceInactivityCondition] = useIsInactiveConfluenceUser();
	const [passesNewConfluenceUserCondition] = useIsNewConfluenceUser();
	const [passesHasNotInteractedWithNudge] = useHasNotInteractedWithNudge();

	useEffect(() => {
		const fetchData = async () => {
			try {
				const confluenceAccess = await checkIfUserHasPermissionToAccessConfluence(cloudId);
				setPermitted(confluenceAccess.permitted);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useConfluenceReactivationExperiment',
						packageName: 'jiraConfluenceReactivationExperiment',
					},
					error,
				});
				setPermitted(false);
			}
		};

		if (
			passesJiraAgeCondition &&
			(passesConfluenceInactivityCondition || passesNewConfluenceUserCondition)
		) {
			fetchData();
		}
	}, [
		cloudId,
		passesJiraAgeCondition,
		passesConfluenceInactivityCondition,
		passesNewConfluenceUserCondition,
	]);

	const isEligibleInactiveUser =
		passesJiraAgeCondition && // jira_first_active + 7 < today
		passesConfluenceInactivityCondition && // should be inactive in Confluence for at least 30 days
		permitted; // should have confluence access

	const isEligibleNewUser =
		passesJiraAgeCondition && // jira_first_active + 7 < today
		passesNewConfluenceUserCondition && // should have never been active in Confluence
		permitted; // should have confluence access

	return {
		isEligibleInactiveUser,
		isEligibleNewUser,
		hasNotInteractedWithNudge: passesHasNotInteractedWithNudge,
	};
};
